import React, { useEffect } from 'react';
import { useParams, Link, useOutletContext } from 'react-router-dom';
import { ArrowRight, ArrowLeft, MapPin, Phone, Check, Home } from 'lucide-react';
import { serviceAreas, services, business } from '../mock';
import useReveal from '../hooks/useReveal';

export default function ServiceAreaDetail() {
  const { slug } = useParams();
  const { openSchedule } = useOutletContext();
  const area = serviceAreas.find((a) => a.slug === slug);
  useReveal();
  useEffect(() => { window.scrollTo({ top: 0 }); }, [slug]);

  if (!area) {
    return (
      <div className="container-x" style={{ paddingTop: 160, paddingBottom: 120, textAlign: 'center' }}>
        <h1 className="text-3xl">Area not found</h1>
        <Link to="/service-areas" className="btn-sage mt-6 inline-flex">All Service Areas</Link>
      </div>
    );
  }

  const others = serviceAreas.filter((a) => a.slug !== slug).slice(0, 3);

  return (
    <>
      <section style={{ paddingTop: 74, backgroundColor: 'var(--cream-2)' }}>
        <div className="container-x py-16 lg:py-20">
          <Link to="/service-areas" className="inline-flex items-center gap-2 text-sm font-medium mb-8" style={{ color: 'var(--muted)' }}>
            <ArrowLeft size={16} /> All Service Areas
          </Link>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="eyebrow mb-2">Serving {area.city}, TX</p>
              <h1 style={{ fontSize: 'clamp(2.2rem, 4.4vw, 3.4rem)' }}>Senior Home Care in {area.city}</h1>
              <p className="mt-5 text-lg" style={{ color: 'var(--muted)' }}>{area.blurb} Our caregivers help your loved one stay safe, comfortable, and happy right at home in {area.city}.</p>
              <div className="flex flex-wrap gap-4 mt-8">
                <button className="btn-sage" onClick={openSchedule}>Free {area.city} Consultation <ArrowRight size={18} /></button>
                <a href={business.phoneHref} className="btn-outline"><Phone size={18} /> {business.phone}</a>
              </div>
            </div>
            <div className="rounded-card" style={{ aspectRatio: '4/3' }}>
              <img src={area.image} alt={`In-home senior care in ${area.city}, Texas`} className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid lg:grid-cols-[1.2fr_1fr] gap-14">
          <div className="reveal">
            <p className="eyebrow mb-2">Why {area.city} Families Choose Us</p>
            <h2 style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.6rem)' }}>Local care you can count on</h2>
            <ul className="flex flex-col gap-4 mt-7">
              {area.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3">
                  <span className="flex items-center justify-center rounded-full mt-0.5 shrink-0" style={{ width: 24, height: 24, backgroundColor: 'var(--sage)' }}>
                    <Check size={14} style={{ color: 'var(--forest)' }} strokeWidth={3} />
                  </span>
                  <span style={{ color: 'var(--ink)' }}>{h}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <p className="font-semibold mb-3" style={{ color: 'var(--forest)' }}>Neighborhoods we serve in {area.city}:</p>
              <div className="flex flex-wrap gap-2.5">
                {area.neighborhoods.map((n) => (
                  <span key={n} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm" style={{ backgroundColor: 'var(--cream-2)', color: 'var(--forest)', border: '1px solid var(--line)' }}>
                    <MapPin size={14} style={{ color: '#5B8C4E' }} /> {n}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="reveal bg-white rounded-3xl p-8" style={{ border: '1px solid var(--line)' }}>
            <h3 className="text-xl mb-5">Care services available in {area.city}</h3>
            <ul className="grid sm:grid-cols-2 gap-3">
              {services.map((s) => (
                <li key={s.id}>
                  <Link to={`/services/${s.id}`} className="flex items-start gap-2.5 text-[15px] group">
                    <Home size={16} style={{ color: '#5B8C4E' }} className="mt-1 shrink-0" />
                    <span className="group-hover:underline" style={{ color: 'var(--ink)' }}>{s.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="section" style={{ backgroundColor: 'var(--cream-2)' }}>
        <div className="container-x">
          <h2 className="text-center" style={{ fontSize: 'clamp(1.8rem, 3.4vw, 2.4rem)' }}>Nearby communities we serve</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {others.map((a) => (
              <Link key={a.slug} to={`/service-areas/${a.slug}`} className="group bg-white rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1" style={{ border: '1px solid var(--line)' }}>
                <div className="relative" style={{ aspectRatio: '16/10' }}>
                  <img src={a.image} alt={`Senior care in ${a.city}`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,42,31,0) 50%, rgba(20,42,31,0.7) 100%)' }} />
                  <span className="absolute bottom-4 left-5 font-serif-display text-xl text-white" style={{ color: '#fff' }}>{a.city}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
